import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3,
  TrendingUp,
  PieChart,
  Activity,
  Users,
  Trophy,
  Target,
  Zap,
  Calendar,
  Filter,
  Download,
  Share2,
  Settings,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  Minus
} from 'lucide-react';
import { useSite } from '../../contexts/SiteContext';

const AnalyticsPage: React.FC = () => {
  const site = useSite();
  const [selectedMetric, setSelectedMetric] = useState('performance');
  const [selectedTimeframe, setSelectedTimeframe] = useState('season');

  // Mock data for demonstration
  const metrics = [
    {
      id: 'performance',
      name: 'Team Performance',
      value: '78.5%',
      change: '+12.3%',
      trend: 'up',
      icon: TrendingUp,
      color: 'text-success'
    },
    {
      id: 'wins',
      name: 'Win Rate',
      value: '65.2%',
      change: '+5.8%',
      trend: 'up',
      icon: Trophy,
      color: 'text-primary'
    },
    {
      id: 'scoring',
      name: 'Avg Points',
      value: '112.4',
      change: '-2.1',
      trend: 'down',
      icon: Target,
      color: 'text-warning'
    },
    {
      id: 'efficiency',
      name: 'Efficiency',
      value: '89.7',
      change: '0.0',
      trend: 'neutral',
      icon: Zap,
      color: 'text-secondary'
    }
  ];

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return <ArrowUpRight className="w-4 h-4" />;
      case 'down':
        return <ArrowDownRight className="w-4 h-4" />;
      default:
        return <Minus className="w-4 h-4" />;
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'up':
        return 'text-success';
      case 'down':
        return 'text-error';
      default:
        return 'text-gray-400';
    }
  };

  return (
    <div className="min-h-screen py-12">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-12"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full border border-primary/30 mb-6"
            >
              <BarChart3 className="w-4 h-4 text-primary" />
              <span className="text-primary text-xs font-semibold uppercase tracking-wider">
                Advanced Analytics
              </span>
            </motion.div>
            
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              <span className="text-white">SPORTS </span>
              <span className="neon-text">ANALYTICS</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Deep dive into performance metrics, trends, and predictive insights across {site.sport || 'all sports'}
            </p>
          </div>

          {/* Controls */}
          <div className="glass-card p-6 mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Metric Category
                </label>
                <select
                  value={selectedMetric}
                  onChange={(e) => setSelectedMetric(e.target.value)}
                  className="w-full px-4 py-2 bg-surface/50 border border-border rounded-lg text-white focus:outline-none focus:border-primary transition-colors"
                >
                  <option value="performance">Team Performance</option>
                  <option value="player">Player Statistics</option>
                  <option value="game">Game Analysis</option>
                  <option value="season">Season Trends</option>
                </select>
              </div>

              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Timeframe
                </label>
                <select
                  value={selectedTimeframe}
                  onChange={(e) => setSelectedTimeframe(e.target.value)}
                  className="w-full px-4 py-2 bg-surface/50 border border-border rounded-lg text-white focus:outline-none focus:border-primary transition-colors"
                >
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="season">This Season</option>
                  <option value="year">This Year</option>
                  <option value="all">All Time</option>
                </select>
              </div>

              <div className="flex gap-2 items-end">
                <button className="px-4 py-2 bg-primary/20 text-primary border border-primary/30 rounded-lg hover:bg-primary/30 transition-all flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Filter
                </button>
                <button className="px-4 py-2 bg-surface/50 text-gray-400 border border-border rounded-lg hover:text-white hover:border-gray-600 transition-all flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Export
                </button>
                <button className="px-4 py-2 bg-surface/50 text-gray-400 border border-border rounded-lg hover:text-white hover:border-gray-600 transition-all flex items-center gap-2">
                  <Share2 className="w-4 h-4" />
                  Share
                </button>
              </div>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {metrics.map((metric, index) => {
              const Icon = metric.icon;
              return (
                <motion.div
                  key={metric.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-card p-6 hover:scale-105 transition-transform cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 ${metric.color}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className={`flex items-center gap-1 ${getTrendColor(metric.trend)}`}>
                      <span className="text-sm font-semibold">{metric.change}</span>
                      {getTrendIcon(metric.trend)}
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-white mb-1">
                    {metric.value}
                  </div>
                  <div className="text-sm text-gray-400">
                    {metric.name}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Performance Chart */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="glass-card p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white">Performance Trends</h3>
                <button className="text-gray-400 hover:text-white transition-colors">
                  <Settings className="w-5 h-5" />
                </button>
              </div>
              <div className="h-64 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <BarChart3 className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>Chart visualization will be implemented here</p>
                  <p className="text-sm mt-2">Connect to data source for live charts</p>
                </div>
              </div>
            </motion.div>

            {/* Distribution Chart */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="glass-card p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white">Score Distribution</h3>
                <button className="text-gray-400 hover:text-white transition-colors">
                  <Settings className="w-5 h-5" />
                </button>
              </div>
              <div className="h-64 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <PieChart className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>Distribution chart will be displayed here</p>
                  <p className="text-sm mt-2">Showing category breakdowns</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Insights Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <Activity className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-white">Key Insights</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-surface/30 rounded-lg border border-border/50">
                <div className="w-2 h-2 rounded-full bg-success mt-2"></div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Upward Performance Trend</h4>
                  <p className="text-gray-400 text-sm">
                    Team performance has increased by 12.3% over the selected timeframe, indicating strong momentum.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 p-4 bg-surface/30 rounded-lg border border-border/50">
                <div className="w-2 h-2 rounded-full bg-primary mt-2"></div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Win Rate Above Average</h4>
                  <p className="text-gray-400 text-sm">
                    Current win rate of 65.2% exceeds the league average by 8.7 percentage points.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 p-4 bg-surface/30 rounded-lg border border-border/50">
                <div className="w-2 h-2 rounded-full bg-warning mt-2"></div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Scoring Opportunity</h4>
                  <p className="text-gray-400 text-sm">
                    Average points per game has decreased slightly. Focus on offensive strategies may improve outcomes.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-border/50">
              <button className="flex items-center gap-2 text-primary hover:text-primary-light transition-colors">
                <span className="font-semibold">View Detailed Report</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default AnalyticsPage;